// EligibilityRequirements.js - Australia
import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, FileText, Briefcase, DollarSign, Languages, GraduationCap } from 'lucide-react';

const EligibilityRequirements = () => {
  const requirements = [
    {
      icon: Briefcase,
      title: 'Nominated Occupation',
      description: 'The occupation you nominate has to appear on the list that applies to your visa, whether that is the MLTSSL, the STSOL or the ROL. A positive skills assessment from the authority designated for that occupation is also needed.',
      applicable: 'GSM, State Nomination'
    },
    {
      icon: Languages,
      title: 'English Ability',
      description: 'Competent English is the floor, meaning IELTS 6.0 in every band or the equivalent. Going beyond it pays off: Proficient (IELTS 7.0) is worth 10 points and Superior (IELTS 8.0) is worth 20 points.',
      applicable: 'All Programs'
    },
    {
      icon: GraduationCap,
      title: 'Education',
      description: 'Your qualifications have to be relevant to the occupation you nominate. A doctorate is worth 20 points and a bachelor degree 15 points, with study completed in Australia earning extra points on top.',
      applicable: 'GSM, State Nomination'
    },
    {
      icon: CheckCircle2,
      title: 'Points Total',
      description: 'Nothing below 65 points can be lodged as an EOI. Being invited is a separate matter: 80-90 points or more is the usual competitive range. Age, English, experience and education all feed the total.',
      applicable: 'GSM (189, 190, 491)'
    },
    {
      icon: DollarSign,
      title: 'Age',
      description: 'You have to be under 45 when the invitation is issued. The 25-32 bracket attracts the full 30 points, dropping to 25 points across 33-39 and 15 points across 40-44.',
      applicable: 'GSM, State Nomination'
    },
    {
      icon: FileText,
      title: 'Health and Character',
      description: 'Everyone sits a medical examination and supplies police certificates for each country they have lived in for 12 months or longer. A substantial criminal record will stop an application.',
      applicable: 'All Programs'
    }
  ];

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-primary mb-4">
            What You Will Need to Qualify
          </h2>
          <p className="text-gray-600 text-base font-light max-w-3xl mx-auto">
            Criteria shift from one visa to the next, but most Australian permanent residency applications come down to the same handful of things.
          </p>
        </motion.div>

        {/* Requirements Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {requirements.map((requirement, index) => {
            const Icon = requirement.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-surface p-6 rounded-lg border border-gray-200 hover:border-secondary transition-colors"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon size={20} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-primary mb-1">
                      {requirement.title}
                    </h3>
                    <span className="text-xs text-secondary font-light">
                      {requirement.applicable}
                    </span>
                  </div>
                </div>
                <p className="text-gray-600 text-sm font-light leading-relaxed">
                  {requirement.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Points Breakdown */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-12 bg-gradient-to-r from-primary to-secondary rounded-lg p-8 text-white"
        >
          <h3 className="text-xl font-semibold mb-4">
            How the Points Add Up
          </h3>
          <p className="text-white/90 text-sm font-light leading-relaxed mb-4">
            Several separate factors feed the Australian points test. 65 points gets you into the pool; realistically you want 80-90 or more to be invited out of it.
          </p>
          <div className="grid md:grid-cols-4 gap-4 text-sm">
            <div className="bg-white/10 rounded-lg p-4">
              <p className="font-semibold mb-1">Age (25-32)</p>
              <p className="text-white/80 font-light">30 points</p>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <p className="font-semibold mb-1">English (Superior)</p>
              <p className="text-white/80 font-light">20 points</p>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <p className="font-semibold mb-1">Experience (8+ years)</p>
              <p className="text-white/80 font-light">20 points</p>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <p className="font-semibold mb-1">Education (Doctorate)</p>
              <p className="text-white/80 font-light">20 points</p>
            </div>
          </div>
        </motion.div>

        {/* Important Note */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 1 }}
          className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-6"
        >
          <p className="text-gray-700 text-sm font-light leading-relaxed">
            <strong>Note:</strong> thresholds and invitation rounds move with immigration targets and demand. We measure your profile against the rules as they stand now and tell you which pathway gives you the strongest chance.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default EligibilityRequirements;
